import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type Section, pathOf, useSectionsOf } from '@/entities/section';
import { groupPath, useGroupsOf, type WorkspaceGroup } from '@/entities/workspace-group';
import type { Workspace } from '@/entities/workspace';

/** Where things are filed, for every workspace the viewer is in (read once, shared by the sections). */
export interface HomePlaces {
  /** Workspace display names (the personal one is not called by its stored name). */
  names: ReadonlyMap<string, string>;
  spaces: ReadonlyMap<string, 'personal' | 'team'>;
  groups: readonly WorkspaceGroup[];
  /** Every section with where it lives ("Workspace / Parent"), for navigation. */
  sections: readonly { section: Section; location: string }[];
  /** "Workspace / Section / Group / Group" of a resource, or of a group (`groupId` = its own place). */
  locationOf: (place: {
    workspace_id: string;
    section_id?: string | null | undefined;
    group_id?: string | null | undefined;
  }) => string;
  /** The same for a group: its ancestors, not itself. */
  groupLocation: (group: WorkspaceGroup) => string;
  ready: boolean;
}

export function useHomePlaces(workspaces: readonly Workspace[]): HomePlaces {
  const { t } = useTranslation();
  const ids = useMemo(() => workspaces.map((w) => w.id), [workspaces]);
  const sectionState = useSectionsOf(ids);
  const groupState = useGroupsOf(ids);
  const sectionData = sectionState.data;
  const groupData = groupState.data;
  const ready = !sectionState.pending && !groupState.pending;
  return useMemo(() => {
    const names = new Map(
      workspaces.map((w) => [
        w.id,
        w.workspace_type === 'personal' ? t('home.personalWorkspace') : w.name,
      ]),
    );
    const spaces = new Map(workspaces.map((w) => [w.id, w.workspace_type] as const));
    const sections = new Map<string, Section[]>();
    const groups: WorkspaceGroup[] = [];
    const groupsBy = new Map<string, WorkspaceGroup[]>();
    workspaces.forEach((w, index) => {
      sections.set(w.id, sectionData[index]?.items ?? []);
      const list = groupData[index]?.items ?? [];
      groupsBy.set(w.id, list);
      groups.push(...list);
    });
    const trail = (
      workspaceId: string,
      sectionId: string | null | undefined,
      groupPathItems: readonly WorkspaceGroup[],
    ) =>
      [
        names.get(workspaceId) ?? '',
        ...pathOf(sections.get(workspaceId) ?? [], sectionId).map((s) => s.name),
        ...groupPathItems.map((g) => g.name),
      ]
        .filter(Boolean)
        .join(' / ');
    const sectionList = workspaces.flatMap((w) =>
      (sections.get(w.id) ?? []).map((section) => ({
        section,
        location: trail(w.id, section.parent_id, []),
      })),
    );
    return {
      names,
      spaces,
      groups,
      sections: sectionList,
      ready,
      locationOf: ({ workspace_id, section_id, group_id }) => {
        const path = groupPath(groupsBy.get(workspace_id) ?? [], group_id);
        return trail(workspace_id, section_id, path);
      },
      groupLocation: (group) =>
        trail(
          group.workspace_id,
          group.section_id,
          groupPath(groupsBy.get(group.workspace_id) ?? [], group.parent_group_id),
        ),
    };
  }, [workspaces, t, ready, sectionData, groupData]);
}
